import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { documentsApi, isCancelled } from './api';
import { DEFAULT_SORT, EMPTY_FILTERS, PAGE_SIZES, VIEW_STORAGE_KEY } from './constants';
import { apiError, sanitizeFilters } from './format';
import { DocumentFilters, DocumentRow, Facets, Pagination, SortKey, SortState } from './types';

const SORT_KEYS: SortKey[] = ['id', 'created', 'updated', 'type', 'status', 'from', 'to', 'author'];

interface StoredView { filters: DocumentFilters; sort: SortState; limit: number }

/** Последний использованный вид списка (фильтры, сортировка, размер страницы) - только удобство, не обязательное состояние */
function loadView(): StoredView {
  try {
    const raw = JSON.parse(localStorage.getItem(VIEW_STORAGE_KEY) || 'null');
    if (raw && typeof raw === 'object') {
      const sortBy = SORT_KEYS.includes(raw.sort?.sortBy) ? raw.sort.sortBy : DEFAULT_SORT.sortBy;
      const sortOrder = raw.sort?.sortOrder === 'asc' ? 'asc' : 'desc';
      const limit = PAGE_SIZES.includes(raw.limit) ? raw.limit : 50;
      return { filters: sanitizeFilters(raw.filters), sort: { sortBy, sortOrder }, limit };
    }
  } catch {
    /* недоступное/битое хранилище - используем значения по умолчанию */
  }
  return { filters: EMPTY_FILTERS, sort: DEFAULT_SORT, limit: 50 };
}

function saveView(view: StoredView) {
  try {
    localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(view));
  } catch {
    /* ignore */
  }
}

/**
 * Список документов: фильтры + сортировка + пагинация.
 *  - поиск применяется с задержкой, устаревшие запросы отменяются;
 *  - фасеты (счётчики статусов/типов) запрашиваются только при смене фильтров, а не при листании страниц;
 *  - в журнал действий пишется только новый набор фильтров: страница, сортировка, лимит и «Обновить» идут с silent;
 *  - при перезагрузке прежние строки остаются на экране (без «мигания»).
 */
export function useDocumentsList(enabled: boolean) {
  const initial = useMemo(loadView, []);
  const [filters, setFilters] = useState<DocumentFilters>(initial.filters);
  const [sort, setSort] = useState<SortState>(initial.sort);
  const [limit, setLimitState] = useState<number>(initial.limit);
  const [page, setPage] = useState(1);
  const [searchApplied, setSearchApplied] = useState(initial.filters.search);
  const [reloadToken, setReloadToken] = useState(0);

  const [rows, setRows] = useState<DocumentRow[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: initial.limit, total: 0, totalPages: 1 });
  const [facets, setFacets] = useState<Facets | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  // Поиск: отложенное применение, чтобы не слать запрос на каждый символ
  useEffect(() => {
    if (filters.search === searchApplied) return undefined;
    const timer = window.setTimeout(() => {
      setSearchApplied(filters.search);
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [filters.search, searchApplied]);

  const effective = useMemo(() => ({ ...filters, search: searchApplied }), [filters, searchApplied]);
  const facetsKey = useMemo(() => JSON.stringify(effective), [effective]);
  const lastFacetsKey = useRef<string>('');
  // Набор фильтров, просмотр которого уже записан в журнал действий
  const lastAuditedKey = useRef<string>('');
  const seq = useRef(0);

  useEffect(() => {
    saveView({ filters, sort, limit });
  }, [filters, sort, limit]);

  useEffect(() => {
    if (!enabled) return undefined;
    const current = ++seq.current;
    const controller = new AbortController();
    const needFacets = lastFacetsKey.current !== `${facetsKey}#${reloadToken}`;
    const silent = lastAuditedKey.current === facetsKey;
    setLoading(true);

    documentsApi
      .list({ filters: effective, ...sort, page, limit, facets: needFacets, silent }, controller.signal)
      .then(data => {
        if (current !== seq.current) return;
        // отмечается только после ответа: отменённый запрос (в т.ч. двойной запуск эффекта в dev) просмотр не «съедает»
        lastAuditedKey.current = facetsKey;
        setRows(data.documents);
        setPagination(data.pagination);
        if (data.facets) {
          setFacets(data.facets);
          lastFacetsKey.current = `${facetsKey}#${reloadToken}`;
        }
        setError(null);
        setForbidden(false);
      })
      .catch(err => {
        if (isCancelled(err) || current !== seq.current) return;
        if (err?.response?.status === 403) setForbidden(true);
        setError(apiError(err, 'Не удалось загрузить документы'));
      })
      .finally(() => {
        if (current === seq.current) setLoading(false);
      });

    return () => controller.abort();
  }, [enabled, effective, facetsKey, sort, page, limit, reloadToken]);

  const patchFilters = useCallback((patch: Partial<DocumentFilters>) => {
    setFilters(prev => ({ ...prev, ...patch }));
    if (!('search' in patch)) setPage(1);
  }, []);

  const replaceFilters = useCallback((next: DocumentFilters) => {
    setFilters(next);
    setSearchApplied(next.search);
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => replaceFilters(EMPTY_FILTERS), [replaceFilters]);

  const toggleSort = useCallback((key: SortKey) => {
    setSort(prev => (prev.sortBy === key
      ? { sortBy: key, sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' }
      : { sortBy: key, sortOrder: key === 'id' || key === 'created' || key === 'updated' ? 'desc' : 'asc' }));
    setPage(1);
  }, []);

  const setLimit = useCallback((value: number) => {
    setLimitState(value);
    setPage(1);
  }, []);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  return {
    filters, effective, patchFilters, replaceFilters, resetFilters,
    sort, toggleSort,
    page, setPage, limit, setLimit,
    rows, pagination, facets,
    loading, error, forbidden, reload
  };
}
